import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson, deleteJson } from '../lib/api';

// --- Types ---

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  category: string;
  body: string;
  variables: string[];
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EmailTemplatesResponse {
  templates: EmailTemplate[];
  total: number;
}

export interface TemplatePreviewData {
  templateId: string;
  sampleData: Record<string, string>;
}

export interface TemplatePreviewResponse {
  subject: string;
  body: string;
}

// --- Hooks ---

export function useEmailTemplates() {
  return useQuery({
    queryKey: ['email-templates'],
    queryFn: () => fetchJson<EmailTemplatesResponse>('/api/email-templates'),
  });
}

export function useEmailTemplate(id: string | null) {
  return useQuery({
    queryKey: ['email-template', id],
    queryFn: () => fetchJson<EmailTemplate>(`/api/email-templates/${id}`),
    enabled: !!id,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<EmailTemplate>) =>
      postJson<EmailTemplate>('/api/email-templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EmailTemplate> }) =>
      putJson<EmailTemplate>(`/api/email-templates/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      queryClient.invalidateQueries({ queryKey: ['email-template', variables.id] });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteJson(`/api/email-templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });
}

export function usePreviewTemplate() {
  return useMutation({
    mutationFn: (data: TemplatePreviewData) =>
      postJson<TemplatePreviewResponse>('/api/email-templates/preview', data),
  });
}
